import { useState } from 'react';
import { LinkPagamento } from '@/components/LinkPagamento';
import { ListaLinks } from '@/components/ListaLinks';
import { Alert, Button } from '@/components/ui';
import type { Capabilities } from '@/lib/crm';
import { formatBRL, formatDate } from '@/lib/format';
import { nomeMetodo, type LinkPagamentoItem, type ParcelaVenda } from '@/lib/pagamentos';
import { useApi } from '@/lib/useApi';

/** Parcelas da venda no Conta Azul e o link de pagamento da parte do cartão. */
export function PagamentoVenda({ saleId, titulo, leadId }: { saleId: string; titulo: string; leadId?: string | null }) {
  const { data: caps } = useApi<Capabilities>('/crm/capabilities');
  const { data: parcelas, error, reload } = useApi<ParcelaVenda[]>(caps?.mercadoPago ? `/crm/sales/${saleId}/parcelas` : null);
  const { data: links, reload: reloadLinks } = useApi<LinkPagamentoItem[]>(caps?.mercadoPago ? `/crm/payment-links?saleId=${saleId}` : null);
  const [aberta, setAberta] = useState<string | null>(null);

  if (!caps?.mercadoPago) return null;
  const total = parcelas?.reduce((a, p) => a + p.valor, 0) ?? 0;
  const emAberto = parcelas?.reduce((a, p) => a + (p.aberta ? p.naoPago : 0), 0) ?? 0;

  return (
    <section className="card">
      <div className="card__head">
        <h2>Pagamento no Conta Azul</h2>
        {parcelas && (
          <span className="muted small">
            {formatBRL(total.toFixed(2))} · em aberto <strong>{formatBRL(emAberto.toFixed(2))}</strong>
          </span>
        )}
      </div>
      {error && <Alert>{error}</Alert>}
      {!parcelas ? (
        !error && <p className="muted">Carregando as parcelas do Conta Azul…</p>
      ) : parcelas.length === 0 ? (
        <p className="muted small">O Conta Azul não devolveu parcelas para esta venda.</p>
      ) : (
        <div className="parcelas">
          {parcelas.map((p) => (
            <div key={p.id} className={`parcela ${p.aberta ? '' : 'is-paid'}`}>
              <div className="parcela__row">
                <div className="parcela__info">
                  <strong>
                    Parcela {p.indice} · {nomeMetodo(p.metodo)}
                  </strong>
                  <span className="muted small">
                    {formatBRL(p.valor.toFixed(2))}
                    {p.vencimento && <> · vence {formatDate(p.vencimento)}</>}
                    {p.aberta ? <> · em aberto {formatBRL(p.naoPago.toFixed(2))}</> : <> · paga</>}
                  </span>
                </div>
                {p.aberta ? (
                  p.linkId ? (
                    <span className="tag-pill tag-pill--amber">Link já gerado</span>
                  ) : (
                    <Button variant={p.cartao ? 'primary' : 'secondary'} onClick={() => setAberta(aberta === p.id ? null : p.id)}>
                      {aberta === p.id ? 'Fechar' : 'Gerar link'}
                    </Button>
                  )
                ) : (
                  <span className="tag-pill tag-pill--green">Paga no Conta Azul</span>
                )}
              </div>
              {aberta === p.id && (
                <div className="parcela__link">
                  {!p.cartao && <p className="muted small">Esta parcela está como {nomeMetodo(p.metodo)} no Conta Azul. Gere o link só se o cliente for pagar esta parte no cartão.</p>}
                  <LinkPagamento
                    saleId={saleId}
                    parcelaId={p.id}
                    leadId={leadId ?? undefined}
                    valorInicial={p.naoPago.toFixed(2)}
                    valorMax={p.naoPago}
                    descricaoInicial={titulo}
                    embutirTaxasPadrao
                    onCreated={() => {
                      void reload();
                      void reloadLinks();
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="muted small">Quando o cliente pagar o link, o sistema marca a parcela como recebida no Conta Azul (conta do Mercado Pago). Não precisa pagar o Pix da fatura.</p>
      {links && links.length > 0 && (
        <>
          <h3 className="links-list__title">Links desta venda</h3>
          <ListaLinks
            links={links}
            onChanged={() => {
              void reloadLinks();
              void reload();
            }}
            mostrarCliente={false}
          />
        </>
      )}
    </section>
  );
}
